import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import adminServices from '../../services/AdminServices';
import Loading from '../feedback/Loading';
import FetchError from '../feedback/FetchError';
import { FaSearch } from 'react-icons/fa';
import OrderModal from '../orders/OrderModal';
import OrdersTable from '../orders/OrdersTable';
import { useAuth } from '../../context/UserContext';
import vendorServices from '../../services/VendorSevices';
import paymentsService from '../../services/PaymentServices';
import OrdersStats from './OrdersStats';

function OrdersTab() {
    const { user } = useAuth()
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [payments, setPayments] = useState(null);
    const [activePayment, setActivePayment] = useState(null);
    const searchInputRef = useRef();
    const navigate = useNavigate();
    const location = useLocation();

    const currentOrdering = searchParams.get('ordering') || '';

    const getPayments = useCallback((queryParams = '') => {
        setError(null);
        setLoading(true);

        let services;

        if (user?.is_admin) {
            services = adminServices;
        } else if (user?.is_vendor) {
            services = vendorServices;
        } else {
            services = paymentsService;
        }

        services.getPurchases(queryParams ? `?${queryParams}` : '').then(
            (res) => {
                setPayments(res.data);
                setLoading(false);
            },
            (err) => {
                console.error('Failed to fetch payments:', err);
                setError(err);
                setLoading(false);
            }
        );
    }, [user]);

    useEffect(() => {
        getPayments();
    }, [getPayments]);

    useEffect(() => {
        const params = searchParams.toString();
        if (params) {
            getPayments(params);
        } else {
            getPayments();
        }
    }, [searchParams, getPayments]);

    const handleSearch = (e) => {
        if (e.key === 'Enter') {
            const query = e.target.value.trim();
            setSearchParams((prev) => {
                const newParams = new URLSearchParams(prev);
                if (query) {
                    newParams.set('search', query);
                } else {
                    newParams.delete('search');
                }
                newParams.delete('page');
                return newParams;
            });
        }
    };

    const changePageParam = (page) => {
        setSearchParams((params) => {
            const newParams = new URLSearchParams(params);
            if (page) {
                newParams.set('page', page);
            } else {
                newParams.delete('page');
            }
            return newParams;
        });
    };

    const openModal = (payment) => {
        setActivePayment(payment);
        setIsModalOpen(true);
    }

    function onClose() {
        setIsModalOpen(false);
        setActivePayment(null);
    }

    const updateURL = (newParams, scroll = false) => {
        const newPath = {
            pathname: location.pathname,
            search: newParams.toString(),
        };
        navigate(newPath, { replace: true, state: { scroll } });
    };

    const handleOrderingChange = (e) => {
        const value = e.target.value;
        const params = new URLSearchParams(searchParams);

        if (value) {
            params.set('ordering', value);
        } else {
            params.delete('ordering');
        }
        params.delete('page');

        updateURL(params);
    };

    return (
        <div className="space-y-6">
            <OrdersStats />
            <div className="flex justify-between items-center">
                <h1 className="text-2xl font-bold text-gray-800">Orders</h1>
                {payments?.count !== undefined && (
                    <p className="text-lg font-semibold text-gray-600">
                        Total Orders: <span className="text-primary">({payments.count})</span>
                    </p>
                )}
            </div>

            <div className="space-y-6">
                {loading ? (
                    <div className="py-9">
                        <Loading />
                    </div>
                ) : error ? (
                    <FetchError error={error} />
                ) : (
                    <div>
                        <div className="flex flex-col md:flex-row items-center justify-between mb-8">
                            <div className="relative w-full md:w-80">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                    <FaSearch />
                                </span>
                                <input
                                    ref={searchInputRef}
                                    className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-primary focus:border-primary"
                                    placeholder="Search by ID, customer, status..."
                                    type="text"
                                    onKeyDown={handleSearch}
                                    defaultValue={searchParams.get('search') || ''}
                                />
                            </div>
                            <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-4 sm:mb-0">
                                <p className="whitespace-nowrap">Sort By:</p>
                                <select
                                    className="select w-full sm:w-auto border"
                                    value={currentOrdering}
                                    onChange={handleOrderingChange}
                                >
                                    <option value="">Default</option>
                                    <option value="-creation_time">Newest</option>
                                    <option value="creation_time">Oldest</option>
                                    <option value="-last_updated_time">Recently Updated</option>
                                    <option value="last_updated_time">Least Recently Updated</option>
                                    <option value="-total">Total: High to Low</option>
                                    <option value="total">Total: Low to High</option>
                                    {(user?.is_admin || user?.is_vendor) && (
                                        <>
                                            <option value="customer_profile__full_name">Customer Name: A to Z</option>
                                            <option value="-customer_profile__full_name">Customer Name: Z to A</option>
                                            <option value="customer_profile__full_email">Email: A to Z</option>
                                            <option value="-customer_profile__full_email">Email: Z to A</option>
                                        </>
                                    )}
                                </select>
                            </div>
                        </div>

                        <OrdersTable
                            payments={payments}
                            changePageParam={changePageParam}
                            openModal={openModal}
                        />

                        <OrderModal
                            payment={activePayment}
                            isOpen={isModalOpen}
                            onClose={onClose}
                            getPayments={getPayments}
                        />

                    </div>
                )}
            </div>
        </div>
    );
}

export default OrdersTab;